import { usePost } from "@albertine/api-client";
import { useState } from "react";
import { useAgents } from "../common/hooks/useAgents";
import type { Agent } from "../common/types/Agent";
import Button from "../components/Common/Buttons";
import Dropdown, { type DropdownOption } from "../components/Common/Dropdown";
import Label from "../components/Common/Label";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";

export function ReassignPlmMembers() {
    const agents = useAgents();
    const [fromAgentId, setFromAgentId] = useState<string | null>(null);
    const [toAgentId, setToAgentId] = useState<string | null>(null);
    const { mutate, isPending, data, error, reset } = usePost("reassignPlmMembers");

    const fromAgent = agents.find((agent) => agent.id === fromAgentId);
    const toAgent = agents.find((agent) => agent.id === toAgentId);
    const canSubmit = !!fromAgent && !!toAgent && fromAgent.id !== toAgent.id;

    const handleReassign = () => {
        if (!fromAgent || !toAgent) return;
        if (
            !window.confirm(
                `Move all members of ${fromAgent.name} to ${toAgent.name}? Members whose tenant ${toAgent.name} can not access stay with ${fromAgent.name}.`,
            )
        ) {
            return;
        }
        mutate({ json: { fromAgentId: fromAgent.id, toAgentId: toAgent.id } });
    };

    return (
        <>
            <Dropdown
                label="Old PLM"
                optionList={toAgentOptions(agents)}
                currentValue={fromAgentId}
                onChange={(value) => {
                    setFromAgentId(value);
                    reset();
                }}
            />
            <Dropdown
                label="New PLM"
                optionList={toAgentOptions(agents.filter((agent) => !agent.disabled))}
                currentValue={toAgentId}
                onChange={(value) => {
                    setToAgentId(value);
                    reset();
                }}
            />
            <Button.Secondary
                label="Move members"
                showLoadingSpinner={isPending}
                disabled={!canSubmit || isPending}
                onClick={handleReassign}
            />

            {(isPending || data || error) && <Label.Large.N10>Results</Label.Large.N10>}

            {isPending && <LoadingSpinner />}
            {!isPending && data && (
                <pre>
                    {`Moved ${data.reassignedMemberIds.length} member(s).`}
                    {data.inaccessibleMembers.length > 0 &&
                        `\nNot moved, because ${toAgent?.name ?? "the new PLM"} can not access their tenant (${data.inaccessibleMembers.length}):\n${data.inaccessibleMembers
                            .map((member) => `${member.memberId} (${member.tenant})`)
                            .join("\n")}`}
                </pre>
            )}
            {!isPending && error && <pre>{JSON.stringify(error, undefined, 2)}</pre>}
        </>
    );
}

function toAgentOptions(agents: Agent[]): DropdownOption[] {
    const agentOptions = agents
        .map((agent) => ({
            value: agent.id,
            label: agent.disabled ? `${agent.name} (disabled)` : agent.name,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label));
    return [{ value: null, label: "Select agent" }, ...agentOptions];
}
